import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import classNames from 'classnames/bind';
import { useTranslation } from 'react-i18next';
import { Icon, Surface, Row } from '@thinkific/toga-react';
import { useRouter } from 'next/router';

import { useCommunityContext } from 'components/CommunityContext';
import {
  LiveEventQuery,
  LiveEventQueryVariables,
} from '__generated__/graphql/legacy/graphql';
import { LIVE_EVENT_QUERY } from 'schema/Event/queries';
import { Col } from 'toga';
import {
  BREAKPOINT_MD,
  EVENT_IMAGE_PLACEHOLDER,
  NUMBER_OF_INTERESTED_USERS_ON_EVENT,
} from 'utils/constants';
import Button, { ButtonWithIconLeft } from 'koba/components/Button';
import EmptyState from 'koba/components/EmptyState';

import Image from 'components/Image';
import PageSkeleton from 'components/PageSkeleton';
import EventContent from 'components/Events/components/EventPanel/components/EventContent/EventContent';
import EventInterestedButton from 'components/Events/components/EventPanel/components/EventInterestedButton/EventInterestedButton';
import EditEventButton from 'components/Events/components/EventPanel/components/EditEventButton/EditEventButton';
import InterestedMembersUpsell from 'components/Upsells/InterestedMembers';
import { goToEvents, goToHome } from 'utils/routerHelpers';
import { isAdmin } from 'utils/userHelpers';
import EventInterestedUsersList from 'components/Events/components/EventInterestedUsersList/EventInterestedUsersList';
import UpcomingEvents from 'components/Events/components/UpcomingEvents';
import CommunityHeader from 'components/CommunityHeader/CommunityHeader';
import FeatureCloak from 'components/VisibilityHelpers/FeatureCloak';
import { DateTime } from 'luxon';
import { features } from 'constants/featureFlags';
import { useSiteFeaturesContext } from 'components/SiteFeaturesContext';
import { useWindowSize } from 'hooks/useWindowSize';
import TrackingEvent from 'constants/trackingEvents';

import styles from './index.module.scss';

const cx = classNames.bind(styles);

export const EventPage = () => {
  const {
    currentUser,
    community,
    featureFlags,
    trackEvent,
  } = useCommunityContext();
  const { width } = useWindowSize();
  const { siteFeatures } = useSiteFeaturesContext();
  const eventNotificationsEnabled =
    siteFeatures?.communityEventNotifications?.isEnabled;

  const rsvpEnabled = featureFlags[features.COMMUNITIES_EVENTS_RSVP];

  const { t } = useTranslation();
  const router = useRouter();
  const { eventId, canUseBrowserBack } = router.query;
  const [hideOtherEventsColumn, setHideOtherEventsColumn] = useState<boolean>(
    false
  );
  const [endCursor, setEndCursor] = useState<string>('');
  const [isValidURL, setIsValidURL] = useState(true);

  const { data, loading, previousData } = useQuery<
    LiveEventQuery,
    LiveEventQueryVariables
  >(LIVE_EVENT_QUERY, {
    variables: {
      eventId: eventId as string,
      numberOfUsers: NUMBER_OF_INTERESTED_USERS_ON_EVENT,
      endCursor,
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network',
    skip: !eventId,
    onError: () => {
      setIsValidURL(false);
    },
  });

  const event = data?.site?.liveEvent;
  const interestedUsers =
    event?.interestedUsers || previousData?.site?.liveEvent.interestedUsers;

  const endTime = DateTime.fromISO(event?.endTime || '');

  const isPastEvent = DateTime.now() > endTime;

  const head = (
    <CommunityHeader
      pageTitle={t('pages-event_page', 'Event {{eventTitle}}', {
        eventTitle: event?.title,
      })}
    />
  );

  if (loading && !previousData) {
    // TODO: Create Skeleton for EventPage
    return (
      <>
        {head}
        <PageSkeleton />;
      </>
    );
  }

  const handleBackButton = () => {
    if (canUseBrowserBack) {
      router.back();
    } else {
      goToHome(router, community.id);
    }
  };

  const showEditButton = isAdmin(currentUser);
  const showEventInterestedButton =
    !isAdmin(currentUser) &&
    !isPastEvent &&
    rsvpEnabled &&
    eventNotificationsEnabled &&
    event; // Show event interest if they are a student, the event is upcoming, RSVP flag is enabled, and communityEventNotifications is enabled

  const hideEventColumn =
    !isAdmin(currentUser) && (!rsvpEnabled || !eventNotificationsEnabled); // Hide the event column if the user is a student, and either RSVP flag or communityEventNotifications is disabled

  const innerCol = hideEventColumn ? 12 : 8;

  const goToEventsPage = () => {
    trackEvent(TrackingEvent.CLICK_SEE_ALL_EVENTS);
    goToEvents(router, community.id);
  };

  return (
    <>
      {head}
      <main className={cx('event-page__container')}>
        {width > BREAKPOINT_MD && (
          <ButtonWithIconLeft
            appearance="knockout"
            className={cx('button__breadcrumb')}
            onClick={handleBackButton}
          >
            <Icon name="arrow-left" />
            {t('common-BACK', 'BACK')}
          </ButtonWithIconLeft>
        )}
        {isValidURL ? (
          <Row className={cx('event-page__wrapper')}>
            <Col lgColumns={8} mdColumns={8}>
              <Surface className={cx('event-page__event-details')}>
                <Row className={cx('event-page__event-details-row')}>
                  <Col
                    className={cx('event-page__event-details-col')}
                    lgColumns={innerCol}
                    mdColumns={innerCol}
                  >
                    <Image
                      alt={event?.imageAltText || ''}
                      className={cx('event-page__image')}
                      height={250}
                      src={event?.imageUrl || EVENT_IMAGE_PLACEHOLDER}
                      width={465}
                    />
                    <div className={cx('event-page__content')}>
                      <h2>{event?.title}</h2>
                      {event && (
                        <EventContent
                          event={event}
                          parentComponent="EventPage"
                        />
                      )}
                    </div>
                  </Col>
                  {!hideEventColumn && (
                    <Col
                      className={cx(
                        'event-page__event-details-col',
                        'event-page__event-details-users-col'
                      )}
                      lgColumns={4}
                      mdColumns={4}
                    >
                      <div className={cx('event-page__action-buttons')}>
                        {showEditButton && (
                          <EditEventButton
                            className="full-width"
                            eventId={event?.id || ''}
                            parentComponent="EventPage"
                            title={t('common-EDIT_EVENT', 'EDIT EVENT')}
                            hasIcon
                          />
                        )}
                        {showEventInterestedButton && (
                          <EventInterestedButton
                            eventId={event?.id}
                            isDisabled={loading}
                            isInterested={event?.isInterested || false}
                          />
                        )}
                      </div>
                      <FeatureCloak
                        communityEventNotifications={false}
                        flags={[
                          features.COMMUNITIES_EVENTS_RSVP,
                          features.COMMUNITIES_EVENT_EMAILS,
                        ]}
                        admins
                      >
                        <div
                          className={cx('event-page__upsell-container')}
                          data-qa="event_page__upsell-container"
                        >
                          <InterestedMembersUpsell />
                        </div>
                      </FeatureCloak>

                      <FeatureCloak
                        flags={[features.COMMUNITIES_EVENTS_RSVP]}
                        communityEventNotifications
                      >
                        <EventInterestedUsersList
                          interestedUsersEdges={interestedUsers?.edges}
                          isLoading={loading}
                          setEndCursor={setEndCursor}
                          totalInterestedUsers={interestedUsers?.totalCount}
                        />
                      </FeatureCloak>
                    </Col>
                  )}
                </Row>
              </Surface>
            </Col>

            {!hideOtherEventsColumn && (
              <FeatureCloak communityEvents>
                <Col
                  className={cx('event-page__other_events-col')}
                  lgColumns={4}
                  mdColumns={4}
                >
                  <h2>
                    {t(
                      'pages-events-other_upcoming_events',
                      'Other upcoming events'
                    )}
                  </h2>
                  <UpcomingEvents
                    numberOfEvents={3}
                    setHideComponent={setHideOtherEventsColumn}
                    shouldDisplayHeader={false}
                  />
                </Col>
              </FeatureCloak>
            )}
          </Row>
        ) : (
          <div className={cx('event-page__empty-state')}>
            <EmptyState
              actions={[
                <Button
                  appearance="default"
                  color={community.primaryColor}
                  dataQA="see-all-events__button"
                  key="1"
                  onClick={goToEventsPage}
                >
                  {t('pages-events-see_all_events', 'See All Events')}
                </Button>,
              ]}
              className="event-page__full-width"
              title={t('pages-events-event_not_found', 'Event not found')}
            />
          </div>
        )}
      </main>
    </>
  );
};

export default EventPage;
